import moment from 'moment';

export const parseDailySchedule = (scheduleHourMinute) => {
  if (!scheduleHourMinute || typeof scheduleHourMinute !== 'string') {
    return [];
  }
  return scheduleHourMinute.split(',').map(v => v.trim()).filter(v => {
    if (v && v.match(/^\d+:\d+$/)) {
      const tmp = v.match(/^(\d+):(\d+)$/);
      const hour = parseInt(tmp[1], 10);
      const minute = parseInt(tmp[2], 10);

      if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
        return true;
      }
    }
    return false;
  });
};

export const calculateDailyNextRun = (times, onInterval) => {
  let nextRunOnAt;
  let prevRunOnAt;

  const now = moment().toISOString();

  const checkingTimes = [];
  for (let time of times) {
    const tmp = time.match(/^(\d+):(\d+)$/);
    const hour = parseInt(tmp[1], 10);
    const minute = parseInt(tmp[2], 10);

    checkingTimes.push(moment().hour(hour).minute(minute).second(0).millisecond(0).subtract(1, 'day').toISOString());
    checkingTimes.push(moment().hour(hour).minute(minute).second(0).millisecond(0).toISOString());
    checkingTimes.push(moment().hour(hour).minute(minute).second(0).millisecond(0).add(1, 'day').toISOString());
  }
  checkingTimes.sort();

  for (let i = 0; i < checkingTimes.length; ++i) {
    nextRunOnAt = checkingTimes[i];

    if (nextRunOnAt > now) {
      prevRunOnAt = checkingTimes[i - 1];
      break;
    }
  }

  const prevRunOffAt = moment(prevRunOnAt).add(onInterval, 'second').toISOString();
  const nowShouldOn = !!onInterval && now < prevRunOffAt;

  const nextRunAt = moment(nowShouldOn ? prevRunOffAt : nextRunOnAt);
  const prevRunAt = moment(nowShouldOn ? prevRunOnAt : prevRunOffAt);

  return {
    prevRunAt,
    nextRunAt,
    nowShouldOn,
  };
};

const calculateNextDailySlotOnly = (times) => {
  if (!times.length) {
    return null;
  }
  const now = moment().toISOString();
  const checkingTimes = [];
  for (let time of times) {
    const tmp = time.match(/^(\d+):(\d+)$/);
    const hour = parseInt(tmp[1], 10);
    const minute = parseInt(tmp[2], 10);

    checkingTimes.push(moment().hour(hour).minute(minute).second(0).millisecond(0).subtract(1, 'day').toISOString());
    checkingTimes.push(moment().hour(hour).minute(minute).second(0).millisecond(0).toISOString());
    checkingTimes.push(moment().hour(hour).minute(minute).second(0).millisecond(0).add(1, 'day').toISOString());
  }
  checkingTimes.sort();

  for (let i = 0; i < checkingTimes.length; ++i) {
    if (checkingTimes[i] > now) {
      return moment(checkingTimes[i]);
    }
  }
  return null;
};

const dailyOnOffPreview = (settings, onIntervalSeconds) => {
  const runDailySchedules = parseDailySchedule(settings.runDailyScheduleHourMinute || '');
  if (!runDailySchedules.length || !onIntervalSeconds || onIntervalSeconds <= 0) {
    return { expectedOn: null, nextRunAt: null, nextAction: null };
  }
  const { nextRunAt, nowShouldOn } = calculateDailyNextRun(runDailySchedules, onIntervalSeconds);
  const nextAction = nowShouldOn ? 'OFF' : 'ON';
  return {
    expectedOn: nowShouldOn,
    nextRunAt,
    nextAction,
  };
};

/**
 * @returns {{ expectedOn: boolean | null, nextRunAt: import('moment').Moment | null, nextAction: 'ON' | 'OFF' | null, isCaptureSchedule?: boolean }}
 */
export const getAutomationSchedulePreview = ({ type, isEnabled, settings }) => {
  if (!isEnabled || !settings || typeof settings !== 'object') {
    return { expectedOn: null, nextRunAt: null, nextAction: null };
  }

  switch (type) {
    case 'MAIN_WATER_PUMP_BASIC':
    case 'FAN_BASIC': {
      const mode = settings.mode;
      if (mode === 'ALWAYS_ON') {
        return { expectedOn: true, nextRunAt: null, nextAction: null };
      }
      if (mode === 'ALWAYS_OFF') {
        return { expectedOn: false, nextRunAt: null, nextAction: null };
      }
      if (mode === 'DAILY') {
        const mins = Number(settings.runOnIntervalMinute);
        if (!Number.isFinite(mins) || mins <= 0) {
          return { expectedOn: null, nextRunAt: null, nextAction: null };
        }
        return dailyOnOffPreview(settings, mins * 60);
      }
      return { expectedOn: null, nextRunAt: null, nextAction: null };
    }
    case 'LED_BASIC': {
      const mode = settings.mode;
      if (mode === 'ALWAYS_ON') {
        return { expectedOn: true, nextRunAt: null, nextAction: null };
      }
      if (mode === 'ALWAYS_OFF') {
        return { expectedOn: false, nextRunAt: null, nextAction: null };
      }
      if (mode === 'DAILY') {
        const hours = Number(settings.runOnIntervalHour);
        if (!Number.isFinite(hours) || hours <= 0) {
          return { expectedOn: null, nextRunAt: null, nextAction: null };
        }
        return dailyOnOffPreview(settings, hours * 3600);
      }
      return { expectedOn: null, nextRunAt: null, nextAction: null };
    }
    case 'NUTRITION_PUMP_BASIC':
    case 'INLET_WATER_BASIC':
    case 'CO2_BASIC': {
      if (settings.mode !== 'DAILY') {
        return { expectedOn: null, nextRunAt: null, nextAction: null };
      }
      const sec = Number(settings.runOnIntervalSecond);
      if (!Number.isFinite(sec) || sec <= 0) {
        return { expectedOn: null, nextRunAt: null, nextAction: null };
      }
      return dailyOnOffPreview(settings, sec);
    }
    case 'CAMERA_BASIC': {
      if (settings.mode !== 'DAILY') {
        return { expectedOn: null, nextRunAt: null, nextAction: null };
      }
      const runDailySchedules = parseDailySchedule(settings.runDailyScheduleHourMinute || '');
      if (!runDailySchedules.length) {
        return { expectedOn: null, nextRunAt: null, nextAction: null };
      }
      const nextRunAt = calculateNextDailySlotOnly(runDailySchedules);
      return {
        expectedOn: null,
        nextRunAt,
        nextAction: null,
        isCaptureSchedule: true,
      };
    }
    case 'PLC_DIRECT':
    default:
      return { expectedOn: null, nextRunAt: null, nextAction: null };
  }
};

const timeStringToMinutes = (time) => {
  const tmp = time.match(/^(\d+):(\d+)$/);
  return parseInt(tmp[1], 10) * 60 + parseInt(tmp[2], 10);
};

const DAY_MINUTES = 24 * 60;

const pushOnWindowFromSlotSeconds = (acc, startMinute, durationSeconds) => {
  if (!durationSeconds || durationSeconds <= 0) {
    return;
  }
  const startSec = startMinute * 60;
  const endSec = startSec + durationSeconds;
  if (durationSeconds >= DAY_MINUTES * 60) {
    acc.push({ startMin: 0, endMin: DAY_MINUTES });
    return;
  }
  if (endSec <= DAY_MINUTES * 60) {
    acc.push({ startMin: startSec / 60, endMin: endSec / 60 });
  } else {
    acc.push({ startMin: startSec / 60, endMin: DAY_MINUTES });
    acc.push({ startMin: 0, endMin: (endSec - DAY_MINUTES * 60) / 60 });
  }
};

const mergeMinuteSegments = (segments) => {
  if (!segments.length) {
    return [];
  }
  const sorted = [...segments].sort((a, b) => a.startMin - b.startMin);
  const out = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    const last = out[out.length - 1];
    if (cur.startMin <= last.endMin) {
      last.endMin = Math.max(last.endMin, cur.endMin);
    } else {
      out.push({ ...cur });
    }
  }
  return out;
};

/**
 * Daily repeating timeline for visualization (minutes from midnight, [0, 1440)).
 * Reflects configured settings only; use UI (e.g. isEnabled) to show whether it is active.
 * @returns {{ segments: Array<{ startMin: number, endMin: number }>, markersMin: number[] }}
 */
export const getAutomationDailyTimeline = ({ type, settings }) => {
  const empty = { segments: [], markersMin: [] };
  if (!settings || typeof settings !== 'object') {
    return empty;
  }

  const segmentsFromDailySchedule = (onIntervalSeconds) => {
    const runDailySchedules = parseDailySchedule(settings.runDailyScheduleHourMinute || '');
    if (!runDailySchedules.length || !onIntervalSeconds || onIntervalSeconds <= 0) {
      return [];
    }
    const raw = [];
    for (const time of runDailySchedules) {
      pushOnWindowFromSlotSeconds(raw, timeStringToMinutes(time), onIntervalSeconds);
    }
    return mergeMinuteSegments(raw);
  };

  switch (type) {
    case 'MAIN_WATER_PUMP_BASIC':
    case 'FAN_BASIC': {
      const mode = settings.mode;
      if (mode === 'ALWAYS_ON') {
        return { segments: [{ startMin: 0, endMin: DAY_MINUTES }], markersMin: [] };
      }
      if (mode === 'ALWAYS_OFF') {
        return empty;
      }
      if (mode === 'DAILY') {
        const mins = Number(settings.runOnIntervalMinute);
        if (!Number.isFinite(mins) || mins <= 0) {
          return empty;
        }
        return { segments: segmentsFromDailySchedule(mins * 60), markersMin: [] };
      }
      return empty;
    }
    case 'LED_BASIC': {
      const mode = settings.mode;
      if (mode === 'ALWAYS_ON') {
        return { segments: [{ startMin: 0, endMin: DAY_MINUTES }], markersMin: [] };
      }
      if (mode === 'ALWAYS_OFF') {
        return empty;
      }
      if (mode === 'DAILY') {
        const hours = Number(settings.runOnIntervalHour);
        if (!Number.isFinite(hours) || hours <= 0) {
          return empty;
        }
        return { segments: segmentsFromDailySchedule(hours * 3600), markersMin: [] };
      }
      return empty;
    }
    case 'NUTRITION_PUMP_BASIC':
    case 'INLET_WATER_BASIC':
    case 'CO2_BASIC': {
      if (settings.mode !== 'DAILY') {
        return empty;
      }
      const sec = Number(settings.runOnIntervalSecond);
      if (!Number.isFinite(sec) || sec <= 0) {
        return empty;
      }
      return { segments: segmentsFromDailySchedule(sec), markersMin: [] };
    }
    case 'CAMERA_BASIC': {
      if (settings.mode !== 'DAILY') {
        return empty;
      }
      const runDailySchedules = parseDailySchedule(settings.runDailyScheduleHourMinute || '');
      if (!runDailySchedules.length) {
        return empty;
      }
      const markersMin = runDailySchedules.map(timeStringToMinutes).sort((a, b) => a - b);
      return { segments: [], markersMin };
    }
    case 'PLC_DIRECT':
    default:
      return empty;
  }
};
